import { useState, type MouseEvent } from 'react'
import { Loader2, UserX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { LoadError } from '@/components/campaigns/LoadError'
import { formatDateTime } from '@/components/campaigns/campaignFormat'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDecideOptOutReview, useOptOutReviewsQuery } from '@/queries/crmCampaigns'
import type { OptOutReview } from '@/services/crmCampaignsService'

/**
 * Quien parece pedir que no le escribamos más. Lo propone un modelo y sus envíos
 * esperan; solo una persona lo convierte en baja. Sin pendientes no ocupa sitio.
 */
export function OptOutReviews() {
  const { t, i18n } = useTranslation()
  const reviews = useOptOutReviewsQuery()
  const decide = useDecideOptOutReview()
  const [toConfirm, setToConfirm] = useState<OptOutReview | null>(null)

  const run = (review: OptOutReview, accion: 'confirmar' | 'descartar', onDone?: () => void) => {
    decide.mutate(
      { id: review.id, accion },
      {
        onSuccess: () => {
          toast.success(accion === 'confirmar' ? t('campaigns.opt_out_reviews.confirmed') : t('campaigns.opt_out_reviews.discarded'))
          onDone?.()
        },
        onError: (error: Error) => toast.error(error.message),
      },
    )
  }

  const confirm = (event: MouseEvent) => {
    // El diálogo sigue abierto si falla, para poder reintentar.
    event.preventDefault()
    if (toConfirm) run(toConfirm, 'confirmar', () => setToConfirm(null))
  }

  if (reviews.isError) return <LoadError error={reviews.error} onRetry={() => void reviews.refetch()} />
  if (!reviews.data?.length) return null

  return (
    <section className="space-y-4 rounded-lg border p-4" data-testid="opt-out-reviews">
      <div>
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <UserX className="h-4 w-4" />
          {t('campaigns.opt_out_reviews.title', { count: reviews.data.length })}
        </h2>
        <p className="text-sm text-muted-foreground">{t('campaigns.opt_out_reviews.description')}</p>
      </div>
      <ul className="divide-y">
        {reviews.data.map((review) => (
          <li key={review.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-1">
              <p className="text-sm">{review.mensaje ?? t('campaigns.opt_out_reviews.no_message')}</p>
              <p className="text-xs text-muted-foreground">
                {review.telefono} · {formatDateTime(review.created_at, i18n.language)}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={decide.isPending && decide.variables?.id === review.id}
                onClick={() => run(review, 'descartar')}
              >
                {t('campaigns.opt_out_reviews.discard')}
              </Button>
              <Button variant="destructive" size="sm" onClick={() => setToConfirm(review)}>
                {t('campaigns.opt_out_reviews.confirm')}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <AlertDialog open={toConfirm !== null} onOpenChange={(open) => (open ? undefined : setToConfirm(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('campaigns.opt_out_reviews.confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('campaigns.opt_out_reviews.confirm_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('campaigns.opt_out_reviews.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} disabled={decide.isPending}>
              {decide.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('campaigns.opt_out_reviews.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
